import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/site";
import Navbar from "@/components/Navbar";
import { SiteFooter } from "@/components/Brand";

const STATUS = {
  pending: { icon: Clock, tone: "bg-amber-50 text-amber-800 border-amber-200", title: "Payment being verified", text: "Our team checks UPI payments manually — usually within a few hours. You'll get an email the moment it's confirmed." },
  confirmed: { icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-800 border-emerald-200", title: "Confirmed!", text: "Check your inbox — your next step is on its way." },
  rejected: { icon: XCircle, tone: "bg-rose-50 text-rose-800 border-rose-200", title: "We couldn't verify this payment", text: "Reply to our email with your UPI transaction ID or a screenshot and we'll sort it out." },
};

const OrderStatus = () => {
  const { ref } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/orders/${ref}`).then((r) => setO(r.data)).catch(() => setErr("We couldn't find that request.")); }, [ref]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
        {err ? <p className="font-heading font-bold text-xl text-slate-900 text-center" data-testid="order-not-found">{err}</p>
          : !o ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          : (() => { const s = STATUS[o.status] || STATUS.pending; return (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-slate-200 p-8" data-testid="order-status-card">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-slate-400">Request reference</p>
              <p className="font-mono font-extrabold text-3xl text-slate-900 mt-1" data-testid="order-ref">{o.order_ref}</p>
              <div className={`mt-6 rounded-2xl border p-5 flex items-start gap-3 ${s.tone}`} data-testid={`order-status-${o.status}`}>
                <s.icon className="w-6 h-6 shrink-0 mt-0.5" />
                <div><p className="font-heading font-bold">{s.title}</p><p className="font-body text-sm mt-1">{s.text}</p></div>
              </div>
              <dl className="mt-6 grid sm:grid-cols-2 gap-4 font-body text-sm">
                <div><dt className="text-slate-400">Item</dt><dd className="text-slate-900 font-semibold" data-testid="order-item">{o.item}</dd></div>
                <div><dt className="text-slate-400">Amount</dt><dd className="text-slate-900 font-semibold">{money(o.amount, o.currency)}</dd></div>
                <div><dt className="text-slate-400">Name</dt><dd className="text-slate-900">{o.name}</dd></div>
                <div><dt className="text-slate-400">Email</dt><dd className="text-slate-900">{o.email}</dd></div>
              </dl>
              {o.status === "confirmed" && o.type === "assessment" && (
                <Link to="/access" className="mt-6 inline-flex items-center gap-2 font-heading font-bold text-primary" data-testid="order-go-access">Enter my access code <ArrowRight className="w-4 h-4" /></Link>
              )}
              <p className="font-body text-xs text-slate-400 mt-8">Save this page — you can revisit it anytime to check the status.</p>
            </motion.div>
          ); })()}
      </main>
      <SiteFooter />
    </div>
  );
};

export default OrderStatus;
